import { constants } from '@adamdotai/core/constants'
import {
    ARABIC_TEXT_REGEX,
    checkUserMeetingPermissions,
} from '@adamdotai/core/utils'
import {
    ActionsIcon,
    AgendaIcon,
    Box,
    DecisionsIcon,
    Flex,
    Stack,
    Text,
    useDisclosure,
} from '@adamdotai/design-system'
import type { EditorState, LexicalEditor } from 'lexical'
import { useTranslations } from 'next-intl'
import {
    type ReactNode,
    type RefObject,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useMutation } from 'urql'
import { useHover } from 'usehooks-ts'
import {
    EditorDirection,
    RichTextInput,
} from '@/components/Editor/Interfaces/RichTextInput/RichTextInput'
import { CommandsToolbar } from '@/components/MeetingRoom/MeetingRoomSidepanel/AICapture/Components/CommandsToolbar/CommandsToolbar'
import { DeleteSummaryItemModal } from '@/components/MeetingRoom/MeetingRoomSidepanel/modals/DeleteSummaryItem'
import { graphql } from '@/generated/gql'
import type { LinkEntityType } from '@/generated/gql/graphql'
import { useTextDirection } from '@/hooks/useTextDirection'
import { useMeeting } from '@/utils/state/meeting-room'

const EDIT_SUMMARY_ITEM = graphql(`
    mutation EditSummaryItem($input: EditHighlightInput!){
        editHighlightV1(input: $input){
            meetingSummary {
                recommendedContentItems {
                    id
                    recommendedTitle
                }
            }
        }
    }
`)

type SummaryItemProps = {
    id: string
    title: string
    type:
        | LinkEntityType.Action
        | LinkEntityType.Note
        | LinkEntityType.Decision
        | LinkEntityType.Agenda
    isSubItem: boolean
    editorReference: RefObject<LexicalEditor>
    isEdited: boolean
    meetingId: string
}

const ICONS: Record<SummaryItemProps['type'], ReactNode> = {
    AGENDA: <AgendaIcon />,
    ACTION: <ActionsIcon />,
    DECISION: <DecisionsIcon />,
    NOTE: null,
}

const SummaryItem = ({
    id,
    editorReference,
    isSubItem,
    title,
    type,
    meetingId,
    isEdited,
}: SummaryItemProps) => {
    const [isEditing, setIsEditing] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const isHovered = useHover(containerRef)
    const deleteSummaryDisclosure = useDisclosure()
    const [, editSummaryItem] = useMutation(EDIT_SUMMARY_ITEM)
    const t = useTranslations()

    const contentDirection = useTextDirection(title)

    const [meeting] = useMeeting()
    const { permissions } = meeting

    const hasEditPermission = checkUserMeetingPermissions({
        permissions,
        protectedAction: 'manageContentSpace',
    })

    const editorDirection = useMemo(
        () =>
            ARABIC_TEXT_REGEX.test(title)
                ? EditorDirection.RTL
                : EditorDirection.LTR,
        [title],
    )

    const handleUpdateSummaryItem = async (
        _editor: LexicalEditor,
        newTitle: string,
    ) => {
        setIsEditing(false)
        await editSummaryItem({
            input: {
                id,
                meetingId,
                contentType: type,
                title: newTitle,
            },
        })
    }

    const handleCancel = (
        editor?: LexicalEditor,
        intialState?: EditorState,
    ) => {
        setIsEditing(false)
        editor.setEditorState(intialState)
    }

    const onEdit = () => {
        setIsEditing(true)
    }

    return (
        <Flex
            gap="1"
            ref={containerRef}
            position="relative"
            ms={isSubItem ? '6' : 0}
            _hover={{
                bg: 'neutral.100',
            }}
            dir={contentDirection}
        >
            <Box
                // this is a workaround to compensate for the padding of paragraph nodes in the rich text input
                pt="1"
            >
                {isSubItem ? <>&#8226;</> : ICONS[type]}
            </Box>
            {isHovered && !isEditing && hasEditPermission && (
                <CommandsToolbar
                    content={title}
                    editorReference={editorReference}
                    onEdit={onEdit}
                    onDelete={deleteSummaryDisclosure.onOpen}
                    addItemTrackingEvent={
                        constants.trackedEvents
                            .addSelectedAiSummaryToContentItem
                    }
                />
            )}
            <Stack gap="0" w={isEditing ? 'full' : 'auto'}>
                <RichTextInput
                    meetingId={meetingId}
                    state={title}
                    isEditingMode={isEditing}
                    onSubmit={handleUpdateSummaryItem}
                    onCancel={handleCancel}
                    shouldHidePersonPlugin
                    shouldHideHashtagPlugin
                    actionAreaProps={{
                        pb: '2',
                    }}
                    direction={editorDirection}
                />
                {isEdited && (
                    <Text color="neutral.1400">({t('common.edited')})</Text>
                )}
            </Stack>
            {deleteSummaryDisclosure.isOpen && (
                <DeleteSummaryItemModal
                    handleDeleteSummary={deleteSummaryDisclosure.onClose}
                    id={id}
                    isOpen
                    onClose={deleteSummaryDisclosure.onClose}
                />
            )}
        </Flex>
    )
}

export { SummaryItem }
